import React from "react";
import {
  Home,
  Settings,
  FolderOpen,
  Package,
  Database,
  BarChart3,
  Search,
  Building2,
  MapPin,
  List,
  Map,
  Palette,
  MessagesSquare,
  Warehouse,
  LineChart,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";

const Breadcrumbs = () => {
  const { translate } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const getBreadcrumbItems = () => {
    const items = [
      {
        label: translate("home") || "Home",
        path: "/",
        icon: Home,
        isActive: location.pathname === "/",
      },
    ];

    if (location.pathname.startsWith("/configs")) {
      items.push({
        label: translate("configurationManagement") || "Configuration Management",
        path: "/configs",
        icon: Settings,
        isActive: location.pathname === "/configs",
      });
    }

    if (location.pathname.startsWith("/files")) {
      items.push({
        label: translate("fileManagement") || "File Management",
        path: "/files",
        icon: FolderOpen,
        isActive: location.pathname.startsWith("/files"),
      });

      const pathParts = location.pathname.split("/").filter(Boolean);
      if (pathParts.length >= 2) {
        const collection = pathParts[1];
        const collectionName =
          collection === "general_information"
            ? translate("generalInformation") || "General Information"
            : collection === "internal_procedures"
            ? translate("internalProcedures") || "Internal Procedures"
            : translate("campaigns") || "Campaigns";

        items.push({
          label: collectionName,
          path: `/files/${collection}`,
          icon: FolderOpen,
          isActive: true,
        });
      }
    }

    if (location.pathname.startsWith("/conversations")) {
      items.push({
        label: translate("conversations") || "Conversations",
        path: "/conversations",
        icon: MessagesSquare,
        isActive: location.pathname === "/conversations",
      });
    }

    if (location.pathname.startsWith("/analytics")) {
      items.push({
        label: translate("analytics") || "Analytics",
        path: "/analytics",
        icon: LineChart,
        isActive: location.pathname === "/analytics",
      });
    }

    if (location.pathname.startsWith("/products")) {
      items.push({
        label: translate("productsManagement") || "Products Management",
        path: "/products",
        icon: Package,
        isActive: location.pathname === "/products",
      });
    }

    if (location.pathname.startsWith("/cache")) {
      items.push({
        label: translate("cacheManagement") || "Cache Management",
        path: "/cache",
        icon: Database,
        isActive: location.pathname === "/cache",
      });
    }

    if (location.pathname.startsWith("/vector-store")) {
      items.push({
        label: translate("vectorStoreStatistics") || "Vector Store Statistics",
        path: "/vector-store",
        icon: BarChart3,
        isActive: location.pathname === "/vector-store",
      });
    }

    if (location.pathname.startsWith("/similarity-search")) {
      items.push({
        label: translate("similaritySearch") || "Similarity Search",
        path: "/similarity-search",
        icon: Search,
        isActive: location.pathname === "/similarity-search",
      });
    }

    if (location.pathname.startsWith("/widget")) {
      items.push({
        label: translate("widgetSettings") || "Widget",
        path: "/widget",
        icon: Palette,
        isActive: location.pathname === "/widget",
      });
    }

    if (location.pathname.startsWith("/tenants")) {
      items.push({
        label: translate("tenantManagement") || "Tenant Management",
        path: "/tenants",
        icon: Building2,
        isActive: location.pathname === "/tenants",
      });
    }


    if (location.pathname.startsWith("/store-locations")) {
      const isOnBasePath = location.pathname === "/store-locations";

      items.push({
        label:
          translate("storeLocationsManagement") || "Store Locations Management",
        path: "/store-locations/list",
        icon: MapPin,
        isActive: isOnBasePath,
      });

      if (location.pathname === "/store-locations/list") {
        items.push({
          label: translate("storeLocationsTabList") || "List",
          path: "/store-locations/list",
          icon: List,
          isActive: true,
        });
      } else if (location.pathname === "/store-locations/map") {
        items.push({
          label: translate("storeLocationsTabMap") || "Map",
          path: "/store-locations/map",
          icon: Map,
          isActive: true,
        });
      } else if (location.pathname === "/store-locations/warehouses") {
        items.push({
          label: translate("storeLocationsTabWarehouses") || "Warehouses",
          path: "/store-locations/warehouses",
          icon: Warehouse,
          isActive: true,
        });
      }
    }

    return items;
  };

  const breadcrumbItems = getBreadcrumbItems();

  if (location.pathname === "/") {
    return null;
  }

  return (
    // As cores vem dos tokens e nao de cinzentos fixos. O ultimo item estava em
    // `text-gray-900` (#111827), que no tema escuro fica sobre #18181b e da
    // 1.00:1, ou seja a mesma luminosidade exacta do fundo: o nome da pagina
    // onde se esta era invisivel em todas as rotas. No claro nada muda, os
    // tokens resolvem para os mesmos cinzentos de antes.
    <nav className="flex items-center text-xs mb-6 pb-4 border-b border-[var(--color-border)] flex-wrap gap-y-1">
      {breadcrumbItems.map((item, index) => {
        const isLast = index === breadcrumbItems.length - 1;
        const uniqueKey = `${item.path}-${index}`;

        return (
          <React.Fragment key={uniqueKey}>
            <button
              onClick={() => navigate(item.path, { state: item.state })}
              aria-current={isLast ? "page" : undefined}
              className={`transition-colors cursor-pointer ${isLast ? "font-medium" : "hover:text-[var(--color-text-primary)]"}`}
              style={{
                color: isLast ? "var(--color-text-primary)" : "var(--color-text-secondary)",
              }}
            >
              {item.label}
            </button>
            {!isLast && (
              <span
                aria-hidden="true"
                className="mx-2 select-none"
                style={{ color: "var(--color-text-muted)" }}
              >
                /
              </span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default Breadcrumbs;
